import { FakedCodelists } from '@administration/codelists/codelists.interface';
import { codelists, Codelists } from '@administration/codelists/codelists.service';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CodeList } from '@common/classes/codelist';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { defaultSelectLabel } from '@common/models/util';
import { ViewMode } from '@common/models/view-mode';
import { CommonService } from '@common/services/common.service';
import { QueryService } from '@common/services/query.service';
import { environment } from '@environments/environment';
import { tapSuccessResult } from '@ngneat/query';
import { ComboBoxComponent, PopupSettings } from '@progress/kendo-angular-dropdowns';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { FileInfo, FileRestrictions, SelectEvent } from '@progress/kendo-angular-upload';
import { addDays } from 'date-fns';
import _ from 'lodash';

export type AppControlType =
    | 'string'
    | 'textarea'
    | 'boolean'
    | 'datetime'
    | 'date'
    | 'number'
    | 'password'
    | 'codelist'
    | 'static'
    | 'select'
    | 'file'
    | 'yesno'
    | 'flag'
    | 'color'
    | 'vesselType'
    | 'seal'
    | 'mots';

export interface ColorPalette {
    [colorHex: string]: string;
}

interface FormPropertyBase<Keys extends keyof any = string> {
    name: Keys;
    type: AppControlType;
    label?: string;
    codelist?: Codelists | FakedCodelists;
    initialValue?: any;
    valueField?: string;
    multi?: boolean;
    time?: boolean;
    pattern?: string;
    allowedExtensions?: string[];
    required?: boolean | ((model: any) => boolean);
    options?: string[] | { value: any; label: string }[];
    placeholder?: string;
    colSpan?: number;
    format?: string | NumberFormatOptions;
    decimals?: number;
    rows?: number;
    nullable?: boolean;
    filter?: (item: any, search: any) => boolean;
    customFilter?: string | ((model: any) => string);
    isDisabled?: (model: any) => boolean;
    disabledDates?: (date: Date) => boolean;
    min?: number;
    max?: number;
    minDate?: Date;
    maxDate?: Date;
    onChange?: (value: any, model: any, dialogRef?: any) => void | Promise<void>;
    maxlength?: number;
}

interface FormPropertyNumber<Keys extends keyof any = string> extends FormPropertyBase<Keys> {
    type: 'number';
    min: number;
    max: number;
    format: string | NumberFormatOptions;
    decimals: number;
}

interface FormPropertyOther<Keys extends keyof any = string> extends FormPropertyBase<Keys> {
    type: Exclude<AppControlType, 'number'>;
}

export type FormProperty<Keys extends keyof any = string> = FormPropertyNumber<Keys> | FormPropertyOther<Keys>;

/**
 * @deprecated Use app-input component or specific inputs instead.
 */
@Component({
    selector: 'app-control',
    templateUrl: 'app-control.component.html',
    styleUrls: ['app-control.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: AppControlComponent, multi: true }],
    encapsulation: ViewEncapsulation.None
})
export class AppControlComponent implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {
    private _date: Date;
    private _omittedCodelistIDs: string[] = []; // List of IDs to ignore when fetching codelists. Eg.: when listing possible remaining choices for languages in translations, when adding a new one
    filteredOptions = [];
    error: string;
    codelistTake = environment.settings.appControl.codelist.take;
    private val: any;
    onChange: any = _.noop;
    isMouseEnter: boolean;
    isClicked: boolean;
    characterCount: number = 0;
    codelist$;
    query$;
    injector = inject(Injector);
    showPassword = signal(false);

    defaultDateFormat: string;
    defaultTimeFormat: string;
    defaultDateTimeFormat: string;

    @ViewChild('combobox') public combobox: ComboBoxComponent;

    @Input() options = [];
    @Input() label: string;
    @Input() type: AppControlType;
    @Input() ngModel: NgModel | any;
    @Input() isDisabled: boolean = false;
    @Input() customRoute?: { path: string; field: string };
    @Input() codelist: Codelists | FakedCodelists;
    @Input() codelistFilter = '';
    @Input() textField = 'label';
    @Input() multi = environment.settings.appControl.multi;
    @Input() required = false;
    @Input() filter: (item: any, search: any) => boolean;
    @Input() customFilter = '';
    @Input() format: string | NumberFormatOptions = '#';
    @Input() min: number;
    @Input() max: number;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() fetch: () => Promise<any[]>;
    @Input() popupSettings: Partial<PopupSettings> = environment.settings.appControl.dropdown.popupSettings;
    @Input() hasValue = false;
    @Input() placeholder: string;
    @Input() initialValue: string;
    @Input() valueField = 'value';
    @Input() time = environment.settings.appControl.time;
    @Input() timeOnly = false;
    @Input() decimals: number; // Note: decimals takes precedence over format. Cannot have different editing vs display precision.
    @Input() showSpinners = false; // Show up and down arrows for number input in edit mode
    @Input() rows: number; // Textarea rows
    @Input() maxlength?: number;
    @Input() click;
    @Input() palette: ColorPalette = {
        '#EB1515': 'red',
        '#ECE93E': 'yellow',
        '#38E02F': 'green',
        '#F3A51E': 'orange',
        '#0096FF': 'blue',
        '#9B51E0': 'purple'
    };
    @Input() futureOnly = false; // Allow only dates in the future
    @Input() selectLabel = (item: CodeList) => defaultSelectLabel(item);
    @Input() modelAsDate = false;
    @Input() disableTooltip = false;
    @Input() pattern: string;
    @Input() useCache = true;
    @Input() disabledDates: (date: Date) => boolean = (date: Date) => this.futureOnly && addDays(date, 1) < new Date();
    @Input() fileRestrictions: FileRestrictions = environment.settings.appControl.fileRestrictions;
    @Input() yesNoValues = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
    ];
    @Input() isFilterable = true;
    @Input() clearButton = true;
    @Input() set omittedCodelistIds(IDs: string[]) {
        // do not omit the ID from the query if it's currently selected in this codelist. This happens when we edit an existing grid row for example.
        this._omittedCodelistIDs = IDs.filter((id) => id !== this.value);
    }
    get omittedCodelistIds() {
        return this._omittedCodelistIDs;
    }
    @Input() isEditMode: boolean = true;

    // For boolean inputs
    @Input() nullable: boolean;
    tape = [true, null, false];

    // we need this as an input to avoid autocomplete in some browsers.
    @Input() autocomplete = 'off';

    @Output() ngModelChange = new EventEmitter<any>();
    // @Output() open = new EventEmitter<any>();
    @Output() customBlur = new EventEmitter<void>();

    @Output() valueChange = new EventEmitter<any>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private cdRef: ChangeDetectorRef,
        private queryService: QueryService,
        private commonService: CommonService
    ) {}

    set value(value) {
        this.val = value;
        this.onChange(value);
        this.valueChange.emit(value); // for custom onChange subscriptions
    }

    get value() {
        return this.val;
    }

    get dateValue() {
        if (_.isDate(this.val)) {
            return this.val;
        }

        if (_.isString(this.val)) {
            const date = new Date(this.val);
            // date = new Date(date.getTime() + (date.getTimezoneOffset() * 60_000));

            if (!date || !this._date || date.getTime() !== this._date.getTime()) {
                this._date = date;
            }

            return this._date;
        }

        return this.val;
    }

    set dateValue(value) {
        this._date = value;
        this.value = _.isDate(value) ? (this.modelAsDate ? value : value.toISOString()) : value;
    }

    get booleanValue() {
        return this.val;
    }

    set booleanValue(value) {
        this.value = this.nullable ? this.tape[(this.tape.indexOf(this.value) + 1) % this.tape.length] : value;
    }

    get colorHexPalette() {
        return Object.keys(this.palette || {});
    }

    get colorLabel() {
        return `${this.label}: ${this.palette?.[this.value?.toUpperCase()]}`;
    }

    async ngOnInit() {
        this.maybeApplyActive();
        this.getDefaultDateFormats();
        this.type ||= 'string';
        if (this.codelist) this.queryCodelistDebounced();

        if (['select', 'codelist'].includes(this.type) && this.fetch && this.value) {
            this.options = this.filteredOptions = [{ code: this.value, label: this.value }];
        }

        if (
            this.isDisabled === undefined &&
            this.activatedRoute.snapshot.data &&
            this.activatedRoute.snapshot.data.mode === ViewMode.view
        ) {
            this.isDisabled = true;
        }
        // Execute after content has been checked
        if (this.initialValue) setTimeout(() => (this.value = this.initialValue));
    }

    ngOnChanges(changes) {
        const ngModelValue = changes?.ngModel?.currentValue;
        if (changes.ngModel) this.writeValue(ngModelValue);

        if (changes?.initialValue?.firstChange) this.writeValue(changes.initialValue.currentValue);

        this.error =
            this.required && ([null, undefined].includes(this.ngModel) || (this.ngModel as any).length === 0)
                ? `${this.label || 'Property'} is required`
                : null;

        if (changes?.options && ['codelist', 'select'].includes(this.type)) {
            this.filteredOptions = this.options;
        }

        if (changes && this.type === 'yesno') this.filteredOptions = this.yesNoValues;

        if (changes?.codelist) {
            const initialValue = changes.initialValue?.currentValue;
            this.filterCodeList(initialValue || '');
        }

        if (changes?.customFilter && this.codelist) {
            this.queryCodelist();
        }

        if (ngModelValue !== this.value && !this.initialValue) this.value = ngModelValue || this.value; // Value change is called from parent component

        this.maybeApplyActive();

        if (['textarea', 'string'].includes(this.type) && this.maxlength) {
            this.characterCount = this.value?.length || 0;
        }
    }

    ngOnDestroy(): void {
        if (this.codelist$) {
            this.codelist$.unsubscribe();
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(): void {}

    writeValue(obj: any): void {
        this.val = obj;
        // To prevent showing 'undefined' string text
        if (['string', 'textarea'].includes(this.type) && this.value === undefined)
            setTimeout(() => (this.value = null));
        this.maybeApplyActive();
    }

    queryCodelist(filter = this.codelistFilter) {
        if (this.codelist$) this.codelist$.unsubscribe();
        this.query$ = this.queryService.getCodelistQuery({
            customFilter: this.customFilter,
            name: this.codelist,
            filter,
            selectedIds: this.value ? [this.value].flat() : null,
            omittedIds: this.omittedCodelistIds,
            injector: this.injector
        }).result$;
        this.codelist$ = this.query$
            .pipe(
                tapSuccessResult<any>((data: []) => {
                    this.options = data.map((x: any) => ({
                        label: this.selectLabel(x),
                        value: x.id,
                        name: x.name
                    }));
                    const currentValue = this.multi ? this.value?.[0] : this.value;
                    this.filteredOptions = this.options.filter(
                        (x, i) => x.value === currentValue || i < this.codelistTake + 1
                    ); // +1 if selected value is not in first 100
                    if (_.isFunction(this.filter))
                        this.filteredOptions = this.filteredOptions.filter((x: any) => this.filter(x, filter));

                    this.handleFilteredOptionsAutoSelect();
                })
            )
            .subscribe();
    }

    queryCodelistDebounced = _.debounce((filter?: any) => this.queryCodelist(filter), 500);

    filterCodeList($event) {
        const searchString = typeof $event === 'number' ? $event : $event.toLowerCase();
        if (this.type === 'codelist') return this.queryCodelistDebounced(searchString);

        const options = this.type === 'select' ? this.options : this.yesNoValues;
        this.filteredOptions = _.take(
            options.filter((o) => o.label.toLowerCase().includes(searchString || '')),
            this.codelistTake
        );

        if (_.isFunction(this.filter)) {
            this.filteredOptions = this.filteredOptions.filter((x: any) => this.filter(x, searchString));
        }

        this.handleFilteredOptionsAutoSelect();

        return Promise.reject(null);
    }

    async onDropdownOpen() {
        if (this.fetch) {
            const data = await this.fetch();
            this.options = this.filteredOptions = data;
        }
    }

    fileSelect(event: SelectEvent) {
        event.files.forEach((file: FileInfo) => {
            this.loadFile(file, (content) => {
                if (this.multi) {
                    this.value.shift();
                    this.value.push({ content, name: file.name });
                } else {
                    this.value = { content, name: file.name };
                }
            });
        });
    }

    private loadFile(file: FileInfo, onLoaded: (content: string) => void) {
        if (file.validationErrors?.length) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            const str = <string>ev.target.result; // data:text/plain;base64,[CONTENT]
            onLoaded(str.substring(str.indexOf(',') + 1));
        };
        reader.readAsDataURL(file.rawFile);
    }

    maybeApplyActive() {
        this.hasValue = ![null, undefined].includes(this.value) && this.value?.length !== 0;
        this.cdRef.detectChanges();
    }

    focusInHandler() {
        this.hasValue = !!this.value || !this.isDisabled;
        this.isClicked = true;
        this.cdRef.detectChanges();
    }

    focusOutHandler() {
        this.maybeApplyActive();
        this.isClicked = false;

        this.customBlur.emit();
    }

    onKeyPress(event: KeyboardEvent) {
        if (!this.pattern) return;
        if (!new RegExp(this.pattern).test(event.key)) event.preventDefault();
    }

    /**
     * Filters array of options to get a string of option labels for view mode
     * @returns {string} string of selected options
     */
    get selectViewValue(): string {
        if (!this.options) return '';
        if (this.multi) {
            return this.value
                ?.map((val) => this.options.find((option) => option.value === val)?.label)
                .filter(Boolean)
                .join(', ');
        } else {
            const selectedItem = this.options.find((item) => item.value === this.value);
            return selectedItem?.label ?? '';
        }
    }

    private getDefaultDateFormats() {
        const settings = this.commonService.queryService.queryClient.getQueryData<Setting[]>(['settings']);
        if (!settings) return;
        const defaultDateFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.DateFormat
        )?.value;
        if (defaultDateFormat) this.defaultDateFormat = defaultDateFormat;
        const defaultTimeFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.TimeFormat
        )?.value;
        if (defaultTimeFormat) this.defaultTimeFormat = defaultTimeFormat;
        this.defaultDateTimeFormat = `${this.defaultDateFormat} ${this.defaultTimeFormat}`;
    }

    getTooltip(value: any, reboundValue: string) {
        if (typeof value === 'number') return;

        const codelistsToExclude = [codelists.port, codelists.country] as string[];
        if (codelistsToExclude.includes(this.codelist)) return reboundValue;

        return value;
    }

    /**
     * Handles auto-selection and clearing logic for filtered options
     * - Clears value if current selection is invalid
     * - Auto-selects if only one option exists and field is required
     */
    private handleFilteredOptionsAutoSelect(): void {
        const currentValue = this.multi ? this.value?.[0] : this.value;
        const isCurrentValueValid = currentValue && this.filteredOptions.some((x) => x.value === currentValue);

        // Clear value only if it's no longer in the filtered options
        if (currentValue && !isCurrentValueValid) {
            this.value = null;
        }

        // Auto-select if only one option and field is required
        if (this.required && this.filteredOptions.length === 1 && !this.value) {
            this.value = this.filteredOptions[0].value;
        }
    }

    togglePasswordVisibility() {
        this.showPassword.set(!this.showPassword());
    }
}
